import { useState } from 'react';
import { IoIosArrowDown } from 'react-icons/io';
import { useAuth } from 'hooks';
import { ConsignmentDetails } from 'types';
import { CopyButton, Heading, Timer } from 'components';
import { formatTimestamp, integerToCurrency } from 'helpers';
import { FontWeight, STATUSES, TEST_IDS, salesMode } from 'commons';
import * as S from '../styles';

interface Props {
  consignment: ConsignmentDetails;
  defaultOpen?: boolean;
}

const UNKNOWW_STATUS = 'Status desconhecido';

export function DashboardHeader({ consignment, defaultOpen = false }: Props) {
  const { user } = useAuth();
  const { isAdmin } = user;
  const [showMore, setShowMore] = useState(defaultOpen);
  const {
    code,
    status,
    reservedEmployee,
    endDate,
    createdDate,
    customer,
    payment,
    vouchers,
    nfeNumber,
  } = consignment;
  const {
    discountsPrice,
    costOfDeliveryPrice,
    installments,
    subTotalPrice,
    transactionId,
  } = payment;
  const hasVoucher = Boolean(vouchers[0]);
  const voucherCode = hasVoucher ? vouchers[0].code : 'Não aplicado';
  const voucherName = hasVoucher ? vouchers[0].name : '';

  const costOfDelivery = integerToCurrency(costOfDeliveryPrice.value);
  const discount = integerToCurrency(discountsPrice.value);
  const subTotal = integerToCurrency(subTotalPrice.value);
  const total = integerToCurrency(subTotalPrice.value - discountsPrice.value);
  const { day, month, hour, minute } = formatTimestamp(createdDate);
  const formattedCreatedDate = `${day}/${month} às ${hour}h${minute}`;

  const isPickup = consignment.salesMode === salesMode.pickup;
  const salesModeText = isPickup ? 'Retire na loja' : 'Entrega pela loja';

  const showReservedEmployee = Boolean(reservedEmployee?.name);

  const showTimerStatuses = [
    STATUSES.AVAILABLE_FOR_STORE.value,
    STATUSES.RESERVED_BY_STORE.value,
  ];
  const showTimer = showTimerStatuses.includes(status);
  const customerName = `${customer.firstName} ${customer.lastName}`;

  function handleShowMore() {
    setShowMore(!showMore);
  }

  return (
    <S.HeaderWrapper $status={status}>
      <S.StatusBar />

      <S.Main data-testid={TEST_IDS.CONSIGNMENT_DETAILS_HEADER}>
        <S.Header>
          <S.CodeWrapper>
            <Heading weight={FontWeight.bold}>{code}</Heading>

            <CopyButton
              data-testid={TEST_IDS.COPY_CONSIGMENT_CODE_BUTTON}
              content={code}
            />
          </S.CodeWrapper>

          <S.LargeWrapper>
            <S.StatusWrapper>
              <p>Status</p>

              <S.Status data-testid={TEST_IDS.CONSIGNMENT_STATUS_TEXT}>
                {STATUSES[status]?.label || UNKNOWW_STATUS}
              </S.Status>
            </S.StatusWrapper>

            <S.Container>
              {showTimer && <Timer endTimer={endDate} />}

              <S.Button $showMore={showMore} onClick={handleShowMore}>
                <IoIosArrowDown />
              </S.Button>
            </S.Container>
          </S.LargeWrapper>
        </S.Header>

        {showMore && (
          <S.Retractable>
            <S.FirstContainer>
              <S.TextContainer>
                <S.BoldText>{salesModeText}</S.BoldText>
              </S.TextContainer>

              <S.TextContainer>
                <p>Cliente:</p>
                <S.BoldText>{customerName}</S.BoldText>
              </S.TextContainer>

              <S.TextContainer>
                <p>NF: </p>
                <S.BoldText>{nfeNumber}</S.BoldText>
              </S.TextContainer>

              <S.TextContainer>
                <p>Pedido em:</p>
                <S.BoldText>{formattedCreatedDate}</S.BoldText>
              </S.TextContainer>

              {showReservedEmployee && (
                <S.TextContainer>
                  <p>Reservado:</p>
                  <S.BoldText>{reservedEmployee.name}</S.BoldText>
                </S.TextContainer>
              )}
            </S.FirstContainer>

            <S.Column>
              <S.TextContainer>
                <p>TOTAL:</p>
                <S.BoldText>{total}</S.BoldText>
              </S.TextContainer>

              <S.TextContainer>
                <p>Parcelas:</p>
                <S.BoldText>{installments}</S.BoldText>
              </S.TextContainer>

              <S.TextContainer>
                <p>Desconto:</p>
                <S.BoldText>{discount}</S.BoldText>
              </S.TextContainer>

              <S.TextContainer>
                <p>Frete:</p>
                <S.BoldText>{costOfDelivery}</S.BoldText>
              </S.TextContainer>

              <S.TextContainer>
                <p>Subtotal:</p>
                <S.BoldText>{subTotal}</S.BoldText>
              </S.TextContainer>
            </S.Column>

            <S.Column>
              <S.TextContainer>
                <p>Cupom:</p>
                <S.TextContainer $column>
                  <S.BoldText>{voucherCode}</S.BoldText>

                  {hasVoucher && <S.BoldText>{voucherName}</S.BoldText>}
                </S.TextContainer>
              </S.TextContainer>
              {isAdmin && (
                <S.TextContainer>
                  <p>Transação:</p>
                  <S.BoldText>{transactionId}</S.BoldText>
                </S.TextContainer>
              )}
            </S.Column>
          </S.Retractable>
        )}
      </S.Main>
    </S.HeaderWrapper>
  );
}
